import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
  Cloud, Server, Shield, Database, Network, Settings,
  CheckCircle, Clock, AlertTriangle, MessageSquare, 
  ArrowRight, Download, FileText, Play, Book, Users,
  Lock, Globe, Key, Target, Brain, ChevronRight,
  Award, Sparkles, ArrowLeft
} from 'lucide-react';
import ChatBot from '../../components/ChatBot';

const translations = {
  es: {
    title: 'AZ-104T00-A: Administrador de Microsoft Azure',
    description: 'Aprende a administrar suscripciones de Azure, asegurar identidades, administrar infraestructura, configurar redes virtuales y más.',
    modules: {
      prerequisites: {
        title: 'Prerrequisitos para administradores de Azure',
        duration: '4 días',
        description: 'Aprende los conceptos fundamentales necesarios para convertirte en un administrador de Azure.'
      },
      identities: {
        title: 'Gestionar identidades y gobernanza en Azure',
        duration: '3 días',
        description: 'Aprende a administrar suscripciones de Azure y recursos, implementar y administrar el almacenamiento.'
      },
      storage: {
        title: 'Implementar y gestionar almacenamiento',
        duration: '3 días',
        description: 'Aprende a implementar y administrar el almacenamiento en Azure.'
      },
      compute: {
        title: 'Implementar y gestionar recursos de computación',
        duration: '4 días',
        description: 'Aprende a implementar y administrar máquinas virtuales y otros recursos de computación.'
      }
    },
    actions: {
      start: 'Comenzar módulo',
      continue: 'Continuar',
      askMentor: 'Preguntar al mentor'
    }
  },
  en: {
    title: 'AZ-104T00-A: Microsoft Azure Administrator',
    description: 'Learn how to manage Azure subscriptions, secure identities, administer infrastructure, configure virtual networking, and more.',
    modules: {
      prerequisites: {
        title: 'Prerequisites for Azure administrators',
        duration: '4 days',
        description: 'Learn the foundational concepts needed to become an Azure administrator.'
      },
      identities: {
        title: 'Manage identities and governance in Azure',
        duration: '3 days',
        description: 'Learn to manage Azure subscriptions and resources, implement and manage storage.'
      },
      storage: {
        title: 'Implement and manage storage',
        duration: '3 days',
        description: 'Learn to implement and manage storage in Azure.'
      },
      compute: {
        title: 'Implement and manage compute resources',
        duration: '4 days',
        description: 'Learn to implement and manage virtual machines and other compute resources.'
      }
    },
    actions: {
      start: 'Start module',
      continue: 'Continue',
      askMentor: 'Ask mentor'
    }
  }
};

const AzureAdminModule1 = () => {
  const [showChatbot, setShowChatbot] = useState(false);
  const [completedSections, setCompletedSections] = useState<string[]>([]);
  const { language } = useLanguage();
  const t = translations[language];
  const navigate = useNavigate();

  // Load completed sections from localStorage
  React.useEffect(() => {
    const saved = localStorage.getItem('completedSections-az104-module1');
    if (saved) {
      setCompletedSections(JSON.parse(saved));
    }
  }, []);

  // Save completed sections to localStorage
  React.useEffect(() => {
    localStorage.setItem('completedSections-az104-module1', JSON.stringify(completedSections));
  }, [completedSections]);

  const sections = [
    {
      id: 'intro',
      title: t.modules.prerequisites.title,
      description: t.modules.prerequisites.description,
      duration: t.modules.prerequisites.duration,
      icon: Book
    },
    {
      id: 'identities',
      title: t.modules.identities.title,
      description: t.modules.identities.description,
      duration: t.modules.identities.duration,
      icon: Key
    },
    {
      id: 'storage',
      title: t.modules.storage.title,
      description: t.modules.storage.description,
      duration: t.modules.storage.duration,
      icon: Database
    }
  ];

  const isSectionAvailable = (index: number) => {
    if (index === 0) return true;
    return completedSections.includes(sections[index - 1].id);
  };

  const getSectionProgress = (sectionId: string) => {
    if (completedSections.includes(sectionId)) return 100;
    return 0;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Module Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg p-6 text-white shadow-lg mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">{t.title}</h1>
              <p className="mt-4 text-white/90 max-w-3xl">{t.description}</p>
            </div>
            <button
              onClick={() => setShowChatbot(true)}
              className="bg-white text-purple-600 px-4 py-2 rounded-lg hover:bg-white/90 transition-colors flex items-center shadow-md hover:shadow-lg"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              {t.actions.askMentor}
            </button>
          </div>

          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white/10 rounded-lg p-4">
              <div className="flex items-center">
                <Target className="h-5 w-5 text-white/80 mr-2" />
                <div>
                  <p className="text-white/80 text-sm">Level</p>
                  <p className="font-medium">Intermediate</p>
                </div>
              </div>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="flex items-center">
                <Clock className="h-5 w-5 text-white/80 mr-2" />
                <div>
                  <p className="text-white/80 text-sm">Duration</p>
                  <p className="font-medium">4 days</p>
                </div>
              </div>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="flex items-center">
                <Globe className="h-5 w-5 text-white/80 mr-2" />
                <div>
                  <p className="text-white/80 text-sm">Languages</p>
                  <p className="font-medium">EN, ES</p>
                </div>
              </div>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="flex items-center">
                <Award className="h-5 w-5 text-white/80 mr-2" />
                <div>
                  <p className="text-white/80 text-sm">Certification</p>
                  <p className="font-medium">AZ-104</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sections Grid */}
        <div className="grid grid-cols-1 gap-6">
          {sections.map((section, index) => {
            const isAvailable = isSectionAvailable(index);
            const progress = getSectionProgress(section.id);

            return (
              <div
                key={section.id}
                className="dark-card p-6 hover:border-accent/50 transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center">
                      <section.icon className="h-5 w-5 text-accent" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-foreground">{section.title}</h3>
                      <p className="text-foreground/70">{section.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 text-foreground/50 mr-2" />
                    <span className="text-sm text-foreground/70">{section.duration}</span>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-foreground/70">Progress</span>
                    <span className="text-sm font-medium text-foreground">{progress}%</span>
                  </div>
                  <div className="h-2 bg-secondary rounded-full">
                    <div
                      className="h-2 bg-accent rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>

                <div className="mt-4 flex justify-end">
                  {!isAvailable ? (
                    <div className="flex items-center text-foreground/50">
                      <Lock className="h-5 w-5 mr-2" />
                      <span>Complete previous section first</span>
                    </div>
                  ) : progress === 100 ? (
                    <button className="inline-flex items-center text-accent hover:text-accent/90">
                      Review
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </button>
                  ) : (
                    <button className="inline-flex items-center text-accent hover:text-accent/90">
                      {progress > 0 ? t.actions.continue : t.actions.start}
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Chatbot */}
      {showChatbot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl">
            <ChatBot 
              role="azure-admin"
              onClose={() => setShowChatbot(false)} 
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AzureAdminModule1;